import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Loader, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";
import ConductionModal from "../ConductionModal";

interface ClassroomSongAssignmentFormProps {
	classroom: Classroom;
	onClose: () => void;
	onAssigned: () => void;
}

const ClassroomSongAssignmentForm: React.FC<ClassroomSongAssignmentFormProps> = ({ classroom, onClose, onAssigned }) => {
	const [songs, setSongs] = useState<Song[] | null>(null);
	const [loadingSongs, setLoadingSongs] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminClassroomSongAssignmentForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			songs: [] as MultiSelectOption[],
		}
	});

	const fetchSongs = () => {
		const notificationId = Utils.displayNotification(
			"Loading Songs",
			undefined,
			"blue",
			"loading"
		);

		ConductionAPI.getAllSongs(token)
			.then((loadedSongs: Song[] | null) => {
				if (!loadedSongs || loadedSongs.length === 0) {
					setSongs(null);
					Utils.updateNotification(
						notificationId,
						"No songs Found",
						undefined,
						"red",
						<IconAlertCircle size={16} />,
						true
					);
					setLoadingSongs(false);
				} else {
					setSongs(
						loadedSongs.sort(
							(a, b) =>
								b.uploadDate.getTime() - a.uploadDate.getTime() // Newest first
						)
					)
					Utils.updateNotification(
						notificationId,
						"Songs Loaded",
						"All songs have been retrieved.",
						"green",
						<IconCheck size={16} />
					);
				}
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching Classrooms",
					"An error occurred while retrieving songs.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
				setLoadingSongs(false);
			});
	}

	useEffect(() => {
		fetchSongs();
	}, [])

	useEffect(() => {
		if (songs === null || classroom === null) {
			return;
		}
		form.values.songs = songs
			.filter(s => classroom.songIds.includes(s.songId))
			.map((s) => {
				const composers = s.artists.filter(a => a.artistType === "Composer");
				const option: MultiSelectOption = {
					label: `"${s.songName}" by ${composers.map((a, i) => `${composers.length > 1 && i === composers.length - 1 ? "and " : ""}${a.artistName}`).join(composers.length > 2 ? ", " : " ")}`,
					value: s.songId
				};
				return option;
			});
		setLoadingSongs(false);
	}, [songs, classroom])

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Assigning Songs to Classroom",
			undefined,
			"blue",
			"loading"
		);

		const songIds: string[] = form.values.songs.map((s) =>
			s.value.toString()
		)

		try {
			await ConductionAPI.updateClassroomSongAssignments(classroom.id, songIds, token);

			Utils.updateNotification(
				notificationId,
				"Assigned Songs",
				undefined,
				"green",
				<IconCheck size={16} />
			);

			onAssigned();
			onClose();
		} catch (error) {
			console.error("Error assigning to classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to assign song to classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const songOptions: MultiSelectOption[] = useMemo(() => songs?.map((s) => {
		const composers = s.artists.filter(a => a.artistType === "Composer");
		const option: MultiSelectOption = {
			label: `"${s.songName}" by ${composers.map((a, i) => `${composers.length > 1 && i === composers.length - 1 ? "and " : ""}${a.artistName}`).join(composers.length > 2 ? ", " : " ")}`,
			value: s.songId
		};
		return option;
	}) ?? [], [songs])

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Assign Songs to Classroom"
			size="md"
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
			>
				{
					(loadingSongs) ? (
						<Loader size="xl" variant="dots" />
					) : classroom === null ? (
						<Text>The needed data did not load correctly.</Text>
					) : (
						<form onSubmit={form.onSubmit(handleSubmit)}>
							<Flex
								direction={"column"}
								justify={"space-between"}
								align={"stretch"}
								style={{
									minHeight: `${height}vh`
								}}
							>
								<Stack>
									<Stack>
										<Text fw={500}>Which songs should be in the library for the {classroom.name} classroom?</Text>
										<MultiSelectCreatable
											options={songOptions}
											value={form.values.songs}
											creatable={false}
											{...form.getInputProps("songs")}
										/>
									</Stack>
								</Stack>
								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={form.submitting}
									color="red"
								>
									Assign Songs to Classroom
								</Button>
							</Flex>
						</form>
					)
				}
			</Box>
		</ConductionModal>
	);
};

export default ClassroomSongAssignmentForm;
